// Колоните на „Разделени маси" — КЛИЕНТСКИ режим: `GET /bills/splits` връща
// целия период наведнъж (масив без страниране), затова сортирането, филтрите
// и редът „Общо" се смятат в браузъра.
//
// Трите падащи менюта над таблицата (от маса, към маса, сервитьор) вече са
// филтри В САМИТЕ КОЛОНИ. Опциите се подават изрично, за да са подредени
// азбучно, а не по реда на появата им в отговора.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type {
  ChecklistOption,
  ChipCellValue,
} from "../../../../components/dataTable";
import type { SplitRow } from "./billsSplitsMath";

const helper = createColumnHelper<SplitRow>();

type Options = {
  fromOptions: ChecklistOption[];
  toOptions: ChecklistOption[];
  waiterOptions: ChecklistOption[];
  /** Периодът е повече от един ден — датата излиза под часа. */
  isMultiDay: boolean;
};

export const useBillsSplitsColumns = ({
  fromOptions,
  toOptions,
  waiterOptions,
  isMultiDay,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      helper.accessor((row) => row.split_date, {
        id: "split_date",
        header: t("reportsPage.date"),
        size: 116,
        meta: {
          cell: { kind: "dt", withDate: isMultiDay ? "always" : "never" },
          mobileRole: "meta",
          excel: { format: "date" },
        },
      }),

      // Масите са чипове, както досега. Стойността под чипа е самото име —
      // затова чеклистът и подредбата „по показан текст" работят без нищо
      // допълнително (`facetValueOf` чете `label`).
      helper.accessor(
        (row): ChipCellValue => ({ tone: "brand", label: row.from_bill_name }),
        {
          id: "from_bill",
          header: t("reportsPage.fromTable"),
          size: 132,
          meta: {
            cell: { kind: "chip" },
            filter: { kind: "checklist", options: fromOptions },
            alwaysVisible: true,
            mobileRole: "title",
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor(
        (row): ChipCellValue => ({ tone: "brand", label: row.to_bill_name }),
        {
          id: "to_bill",
          header: t("reportsPage.toTable"),
          size: 132,
          meta: {
            cell: { kind: "chip" },
            filter: { kind: "checklist", options: toOptions },
            alwaysVisible: true,
            mobileRole: "title",
            excel: { format: "text" },
          },
        },
      ),

      helper.accessor((row) => row.waiterLabel, {
        id: "waiter",
        header: t("reportsPage.waiter"),
        size: 160,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist", options: waiterOptions },
          hideBelow: "narrow",
          mobileRole: "meta",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.itemsCount, {
        id: "items",
        header: t("reportsPage.products"),
        size: 104,
        meta: {
          cell: { kind: "num", decimals: 0 },
          filter: { kind: "numberRange" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.amount, {
        id: "amount",
        header: t("reportsPage.amount"),
        size: 132,
        meta: {
          cell: { kind: "money", strong: true },
          filter: { kind: "numberRange" },
          total: "sum",
          alwaysVisible: true,
          mobileRole: "strong",
          excel: { format: "money" },
        },
      }),

      // Коментарът към разделянето досега не се виждаше НИКЪДЕ. Колоната е
      // изгасена по подразбиране — пали се с една отметка от панела „Колони",
      // а целият текст стои и в модала с продуктите.
      helper.accessor((row) => row.comment ?? "", {
        id: "comment",
        header: t("reportsPage.billsSplitsComment"),
        size: 220,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          defaultHidden: true,
          mobileRole: "hidden",
          excel: { format: "text" },
        },
      }),
    ],
    [t, fromOptions, toOptions, waiterOptions, isMultiDay],
  );
};
